import React, { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { any, object, TypeOf, z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import { green } from "@mui/material/colors";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  FormHelperText,
  Checkbox,
  ListItemText,
} from "@mui/material";
import TokenService from "../../utility";
import {
  saveCustomfield,
  updateCustomfield,
} from "../../redux/actions/customfield.actions";
import { loadSettings } from "../../redux/actions/settings.actions";

interface Props {
  isDialogOpened: boolean;
  handleCloseDialog: any;
  action: string;
  filterData: any;
}
const formSchema = object({
  id: any(),
  field_label: z.string().min(1, "Field label is required."),
  field_type: z.string().min(1, "Field Type is required"),
  required_field: z.string().min(1, "Required is required."),
  field_options: z.any(),
  file_extensions: z.any(),
  company: z.any(),
  field_status: z.any(),
  field_metadata: z.any(),
  signup_field: z.any(),
})
  .partial()
  .superRefine((data, ctx) => {
    if (data.field_type === "select" && data.field_options === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["field_options"],
        message: "Drop Down Options field is required",
      });
    } else if (
      data.field_type === "file" &&
      data.file_extensions.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["file_extensions"],
        message: "File Types field is required",
      });
    }
  });

/*.refine((data) => data.field_type === 'select' && data.field_options === '', {
    path: ["field_options"],
    message: "Field options is required"
});*/

type FormInput = TypeOf<typeof formSchema>;

const requiredList = [
  { id: "1", name: "Yes" },
  { id: "2", name: "No" },
];

const fieldTypeList = [
  { id: "text", name: "TextField" },
  { id: "textarea", name: "TextArea" },
  { id: "select", name: "DropDown" },
  { id: "multiple", name: "Multiple Select" },
  { id: "file", name: "FileUpload" },
];

const extensionList = [
  { id: "csv", name: "csv" },
  { id: "doc", name: "doc/docx" },
  { id: "gif", name: "gif" },
  { id: "odt", name: "odt" },
  { id: "pdf", name: "pdf" },
  { id: "png", name: "png" },
  { id: "txt", name: "txt" },
  { id: "jpg", name: "jpg/jpeg" },
  { id: "xls", name: "xlx/xlsx" },
];

const statusList = [
  { id: 1, name: "Active" },
  { id: 2, name: "InActive" },
];

const singUpList = [
  { id: 1, name: "Show" },
  { id: 2, name: "Hide" },
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export function CustomForm(props: Props) {
  const { handleCloseDialog } = props;

  const dispatch = useDispatch();
  const { customfield, save } = useSelector((state: any) => state.customfield);
  const loading = useSelector((state: any) => state.UI.loading);
  const [toggleSelectOpt, setToggleSelectOpt] = useState(false);
  const [toggleFileOpt, setToggleFileOpt] = useState(false);
  const [fileExtension, setFileExtension] = React.useState<string[]>([]);
  const {
    register,
    reset,
    setValue,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInput>({ resolver: zodResolver(formSchema) });
  const user = TokenService.getTokenUser();
  const globalSettings = useSelector(
    (state: any) => state.sitesetting.settings
  );
  const [metaData, setMetaData] = React.useState<string[]>([]);

  const defaultValues = useMemo(
    () => ({
      field_label: "",
      field_type: "",
      required_field: "",
      field_options: "",
      file_extensions: [],
      company: user.company,
      field_status: "",
      signup_field: 0,
    }),
    [user]
  );

  const handleClose = useCallback(() => {
    handleCloseDialog(false);
    reset(defaultValues);
    setToggleSelectOpt(false);
    setToggleFileOpt(false);
    setFileExtension([]);
  }, [handleCloseDialog, defaultValues, reset]);

  useEffect(() => {
    dispatch<any>(loadSettings());
  }, [dispatch]);

  useEffect(() => {
    if (globalSettings) {
      globalSettings.forEach((item: any) => {
        if (item["config_name"] === "site") {
          const configvalue = JSON.parse(item["config_value"]);
          if (configvalue) {
            setMetaData(
              configvalue["metadata"].split(/\r?\n/).map((metadata: any) => {
                return metadata;
              })
            );
          }
        }
      });
    }
  }, [globalSettings]);

  useEffect(() => {
    if (save) {
      handleClose();
    }
  }, [save, handleClose]);

  useEffect(() => {
    if (
      customfield &&
      customfield.file_extensions &&
      typeof customfield?.file_extensions === "string"
    ) {
      customfield.file_extensions = customfield?.file_extensions?.split(",");
      setFileExtension(customfield.file_extensions);
    }
    if (customfield) {
      customfield.field_status = customfield.field_status ? 1 : 2;
      customfield.signup_field = customfield.signup_field ? 1 : 2;
    }

    reset(customfield);

    if (
      customfield &&
      (customfield?.field_type === "select" ||
        customfield?.field_type === "multiple")
    ) {
      setToggleSelectOpt(true);
    } else {
      setToggleSelectOpt(false);
    }

    if (customfield && customfield?.field_type === "file") {
      setToggleFileOpt(true);
    } else {
      setToggleFileOpt(false);
    }
  }, [customfield, reset]);

  const onFormSubmit: SubmitHandler<FormInput> = (data) => {
    if (data.file_extensions) {
      data.file_extensions = data.file_extensions.toString();
    }
    data.field_status = data.field_status === 1 ? 1 : 0;
    data.signup_field = data.signup_field === 1 ? 1 : 0;

    if (data.id) {
      dispatch<any>(updateCustomfield(data, data.id, props.filterData));
    } else {
      dispatch<any>(saveCustomfield(data, props.filterData));
    }
  };

  const handleChange = (e: any) => {
    let opt =
      e.target.value === "select" || e.target.value === "multiple"
        ? true
        : false;
    setToggleSelectOpt(opt);
    opt = e.target.value === "file" ? true : false;
    setToggleFileOpt(opt);
  };

  const handleChangEvent = (event: SelectChangeEvent<typeof fileExtension>) => {
    const {
      target: { value },
    } = event;
    setFileExtension(typeof value === "string" ? value.split(",") : value);
    setValue(
      "file_extensions",
      typeof value === "string" ? value.split(",") : value
    );
  };

  return (
    <Dialog open={props.isDialogOpened} onClose={handleClose} fullWidth>
      <DialogTitle>{props.action}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id="customfieldform"
          name="customfieldform"
          noValidate
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <TextField
            id="company"
            type="hidden"
            sx={{ display: "none" }}
            {...register("company")}
            value={user.company}
          />

          <TextField
            autoFocus
            margin="dense"
            id="field_label"
            label="Lable"
            fullWidth
            variant="standard"
            required
            {...register("field_label")}
            error={!!errors["field_label"]}
            helperText={
              errors["field_label"] ? errors["field_label"].message : ""
            }
          />

          <FormControl
            fullWidth
            className="custom_select"
            variant="standard"
            sx={{ mt: 2 }}
            error={!!errors["field_type"]}
          >
            <InputLabel id="status-select-label" className="mb-2">
              Field Type *
            </InputLabel>
            <Controller
              name="field_type"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  defaultValue="1"
                  labelId="Field Type"
                  className="input-container"
                  id="field_type"
                  size="small"
                  disabled={!fieldTypeList?.length}
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    handleChange(e);
                  }}
                >
                  {fieldTypeList.map((c, i) => (
                    <MenuItem key={`c-${i}`} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <FormHelperText>
              {errors["field_type"] ? errors["field_type"].message : ""}
            </FormHelperText>
          </FormControl>

          <FormControl
            fullWidth
            className="custom_select"
            variant="standard"
            sx={{ mt: 2 }}
            error={!!errors["field_type"]}
          >
            <InputLabel id="status-select-label" className="mb-2">
              Metadata *
            </InputLabel>
            <Controller
              name="field_metadata"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  defaultValue="1"
                  labelId="Metadata"
                  className="input-container"
                  id="field_metadata"
                  size="small"
                  disabled={!metaData?.length}
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    //handleChange(e)
                  }}
                >
                  {metaData.map((c, i) => (
                    <MenuItem key={`c-${i}`} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <FormHelperText>
              {errors["field_type"] ? errors["field_type"].message : ""}
            </FormHelperText>
          </FormControl>
          {toggleSelectOpt && (
            <>
              <TextField
                autoFocus
                margin="dense"
                id="field_options"
                label="Options"
                fullWidth
                variant="standard"
                required
                {...register("field_options")}
                error={!!errors["field_options"]}
                helperText={
                  errors["field_options"] ? errors["field_options"].message : ""
                }
              />
              <FormHelperText>
                Comma seperated values. Eg. Option1,Option2
              </FormHelperText>
            </>
          )}
          {toggleFileOpt && (
            <FormControl
              fullWidth
              variant="standard"
              sx={{ mt: 2 }}
              error={!!errors["file_extensions"]}
            >
              <InputLabel id="demo-multiple-checkbox-label">
                File Types
              </InputLabel>
              <Controller
                name="file_extensions"
                control={control}
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    labelId="demo-multiple-checkbox-label"
                    id="demo-multiple-checkbox"
                    multiple
                    value={fileExtension}
                    onChange={handleChangEvent}
                    renderValue={(selected: any) => selected.join(", ")}
                    MenuProps={MenuProps}
                  >
                    {extensionList.map((ext, i) => (
                      <MenuItem key={`c-${i}`} value={ext.id}>
                        <Checkbox
                          checked={fileExtension.indexOf(ext.id) > -1}
                        />
                        <ListItemText primary={ext.name} />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText>
                {errors["file_extensions"]
                  ? errors["file_extensions"].message
                  : ""}
              </FormHelperText>
            </FormControl>
          )}
          <FormControl
            fullWidth
            className="custom_select"
            variant="standard"
            sx={{ mt: 2 }}
            error={!!errors["required_field"]}
          >
            <InputLabel id="status-select-label" className="mb-2">
              Mandatory *
            </InputLabel>
            <Controller
              name="required_field"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  labelId="Required"
                  className="input-container"
                  id="required_field"
                  size="small"
                  value={value}
                  onChange={onChange}
                >
                  {requiredList.map((c, i) => (
                    <MenuItem key={`c-${i}`} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <FormHelperText>
              {errors["required_field"] ? errors["required_field"].message : ""}
            </FormHelperText>
          </FormControl>

          <FormControl
            fullWidth
            className="custom_select"
            variant="standard"
            sx={{ mt: 2 }}
          >
            <InputLabel id="status-select-label" className="mb-2">
              Status *
            </InputLabel>
            <Controller
              name="field_status"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  defaultValue="1"
                  labelId="Status"
                  className="input-container"
                  id="field_status"
                  size="small"
                  disabled={!statusList?.length}
                  value={value}
                  onChange={onChange}
                >
                  {statusList.map((c, i) => (
                    <MenuItem key={`c-${i}`} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>

          <FormControl
            fullWidth
            className="custom_select"
            variant="standard"
            sx={{ mt: 2 }}
          >
            <InputLabel id="status-select-label" className="mb-2">
              SignUp Form*
            </InputLabel>
            <Controller
              name="signup_field"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  defaultValue="1"
                  labelId="SigunUp Form"
                  className="input-container"
                  id="signup_field"
                  size="small"
                  disabled={!singUpList?.length}
                  value={value}
                  onChange={onChange}
                >
                  {singUpList.map((c, i) => (
                    <MenuItem key={`c-${i}`} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>

          <DialogActions>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button
                type="submit"
                form="customfieldform"
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                Save
              </Button>
              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
            <Button
              onClick={handleClose}
              variant="contained"
              color="error"
              disabled={loading}
            >
              Cancel
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
